/* Spin & Save.
 *
 * The browser does not decide anything here. It asks the server for an index,
 * then spins the wheel to that index. Every interesting rule — the odds, one
 * spin per account, minting the discount code with its minimum-spend — lives
 * on the other side of that request, because a wheel that draws in JavaScript
 * is a wheel where the console awards itself the RM 1,999 voucher.
 *
 * TWO STEPS, matching spin.DRAW_CONTRACT. /draw reveals which prize was won
 * immediately — the customer sees what they landed on — but returns no code.
 * The code only exists once /claim is called, which happens only when the
 * customer explicitly accepts. Between those two moments they may take
 * "One More Chance" instead: that re-draws, and is allowed exactly once,
 * because if the code were handed over before that choice, accepting a
 * respin afterwards would leave the customer holding two codes for one
 * entry.
 *
 * STOREFRONT FALLBACK. With no endpoint configured the wheel draws locally
 * and returns the Shopify discount code stored on the selected prize. This
 * keeps the campaign usable without the optional draw/claim service. If an
 * endpoint is configured later, the server response remains authoritative.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-ef-spin]');
  if (!root) return;

  var cfgEl = document.querySelector('[data-ef-spin-cfg]');
  var cfg = {};
  try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }

  var wheel = root.querySelector('[data-ef-spin-wheel]');
  var go = root.querySelector('[data-ef-spin-go]');
  var claimBtn = root.querySelector('[data-ef-spin-claim]');
  var againBtn = root.querySelector('[data-ef-spin-again]');
  var panel = root.querySelector('[data-ef-spin-panel]');
  var nameEl = root.querySelector('[data-ef-spin-name]');
  var photoEl = root.querySelector('[data-ef-spin-photo]');
  var codeWrap = root.querySelector('[data-ef-spin-code]');
  var codeText = root.querySelector('[data-ef-spin-code-text]');
  var copyBtn = root.querySelector('[data-ef-spin-copy]');
  var confettiEl = root.querySelector('[data-ef-spin-confetti]');
  var n = cfg.prizes.length;
  var seg = 360 / n;
  var spinning = false;
  var respun = false;
  var selectedPrize = null;

  var REASONS = {
    already_spun: 'This account has already taken its spin.',
    already_claimed: 'This prize has already been claimed.',
    not_signed_in: 'Please sign in to spin.',
    not_open_yet: 'Spin & Save has not opened yet.',
    missing_code: 'Prize code is not configured. Please contact us.'
  };

  function announce(text, isError) {
    panel.hidden = false;
    photoEl.hidden = true;
    nameEl.textContent = text;
    nameEl.classList.toggle('is-error', !!isError);
  }

  /* The wheel is only ever rotated forwards, and the resting angle is kept so
   * a second spin continues from where the first stopped rather than snapping
   * back to zero before it starts. */
  var angle = 0;

  function spinTo(index) {
    /* The rotation needed is relative to where the wheel IS, not relative to
     * a fresh wheel at rest — angle carries over from the previous spin, so
     * a flat "360 - index*seg" delta (correct only from a zero baseline) put
     * the PREVIOUS result's own offset back into play every respin: landing
     * on index 6 then respinning to index 4 rotated by 4's delta on top of
     * 6's already-resting position, so the wheel visually parked on
     * (6 + 4) mod 7 = 3 while the reveal correctly announced 4 — a real
     * mismatch a customer could see, found live (5,000mAh shown as landed,
     * 10,000mAh revealed). Fixed by computing the delta FROM the wheel's
     * current mod-360 position TO the absolute angle that puts `index` at
     * the pointer, so every spin is correct regardless of where the wheel
     * already was. */
    var wantMod = (360 - index * seg) % 360;
    var curMod = angle % 360;
    var diff = wantMod - curMod;
    if (diff <= 0) diff += 360;
    var target = 360 * 5 + diff;
    angle += target;
    var reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      /* No 4-second flourish for someone who asked for less motion — the
       * result is the point, the animation is decoration. */
      wheel.style.transition = 'none';
      wheel.style.transform = 'rotate(' + angle + 'deg)';
      return Promise.resolve();
    }
    wheel.style.transition = 'transform 4.2s cubic-bezier(0.16, 1, 0.3, 1)';
    wheel.style.transform = 'rotate(' + angle + 'deg)';
    return new Promise(function (res) {
      var done = false;
      var finish = function () { if (!done) { done = true; res(); } };
      wheel.addEventListener('transitionend', finish, { once: true });
      /* transitionend does not fire if the tab is hidden mid-spin, and the
       * result would then never be announced. */
      setTimeout(finish, 4600);
    });
  }

  /* Fires once, the instant the wheel stops. Brand colours only — the wheel's
   * own blue/orange plus white, never rainbow confetti, which would put
   * hues in the page this brand doesn't have. DOM spans animated via
   * transform/opacity only (CSS handles the animation, not JS on a timer),
   * so this costs nothing once the elements are removed. Respects
   * prefers-reduced-motion the same way spinTo() does — skipped entirely,
   * not just shortened, since a burst of motion is the one thing that
   * request is specifically asking to not see. */
  var CONFETTI_COLORS = ['var(--ef-blue)', 'var(--ef-blaze)', 'var(--ef-white)', 'var(--ef-blue-300)'];
  function confetti() {
    if (!confettiEl) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    confettiEl.textContent = '';
    var n = 28;
    for (var i = 0; i < n; i++) {
      var el = document.createElement('span');
      var angle = Math.random() * Math.PI * 2;
      var dist = 60 + Math.random() * 90;
      el.style.setProperty('--ef-confetti-x', (Math.cos(angle) * dist).toFixed(0) + 'px');
      el.style.setProperty('--ef-confetti-y', (Math.sin(angle) * dist + 60).toFixed(0) + 'px');
      el.style.setProperty('--ef-confetti-r', (Math.random() < 0.5 ? '-' : '') + (360 + Math.random() * 360).toFixed(0) + 'deg');
      el.style.setProperty('--ef-confetti-delay', (Math.random() * 150).toFixed(0) + 'ms');
      el.style.setProperty('--ef-confetti-c', CONFETTI_COLORS[i % CONFETTI_COLORS.length]);
      confettiEl.appendChild(el);
    }
    setTimeout(function () { confettiEl.textContent = ''; }, 1400);
  }

  /* Weighted, not uniform — cfg.prizes[i].weight is how many "tickets" that
   * prize holds, so a 35 next to a 4 is roughly 9x more likely, not an equal
   * 1-in-7. Only prizes both `live` (a real code exists) and weight > 0 are
   * eligible; the held-back backpack carries weight 0 for exactly this
   * reason, so it can never be drawn even in preview. This mirrors
   * spin.PRIZES in tools/theme/spin.py — change the odds there, not here.
   * This is a rehearsal of the real draw, not the real draw: an armed server
   * must apply the same weighting itself, since the browser is never the
   * one deciding a live prize. */
  function weightedIndex() {
    var pool = [], total = 0, i;
    for (i = 0; i < cfg.prizes.length; i++) {
      var p = cfg.prizes[i];
      if (p.live && p.weight > 0) { pool.push(i); total += p.weight; }
    }
    if (!pool.length) return Math.floor(Math.random() * n);
    var r = Math.random() * total;
    for (i = 0; i < pool.length; i++) {
      r -= cfg.prizes[pool[i]].weight;
      if (r <= 0) return pool[i];
    }
    return pool[pool.length - 1];
  }

  function draw() {
    if (!cfg.endpoint) {
      var i = weightedIndex();
      return Promise.resolve({ ok: true, index: i, canRespin: !respun, preview: true });
    }
    return fetch(cfg.endpoint + '/draw', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // 'include', not 'same-origin' — the draw/claim server is a
      // different origin from the storefront (a separate Cloud Run
      // service), and 'same-origin' silently drops the "have you already
      // spun" cookie on every cross-origin request, breaking the one-spin
      // limit without ever surfacing an error.
      credentials: 'include',
      body: '{}'
    }).then(function (r) {
      return r.json().then(function (j) { j.status = r.status; return j; });
    });
  }

  function claim() {
    if (!cfg.endpoint) {
      if (!selectedPrize || !selectedPrize.code) {
        return Promise.resolve({ ok: false, reason: 'missing_code' });
      }
      return Promise.resolve({ ok: true, code: selectedPrize.code });
    }
    return fetch(cfg.endpoint + '/claim', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: '{}'
    }).then(function (r) {
      return r.json().then(function (j) { j.status = r.status; return j; });
    });
  }

  /* Shows what was won straight away — every spin reveals its prize. The
   * CODE is the part that waits for an explicit accept. */
  function reveal(prize, canRespin) {
    nameEl.classList.remove('is-error');
    nameEl.textContent = prize.note || prize.label || 'You won.';
    if (prize.photo) {
      photoEl.src = prize.photo;
      photoEl.hidden = false;
    } else {
      photoEl.hidden = true;
      photoEl.removeAttribute('src');
    }
    panel.hidden = false;
    codeWrap.hidden = true;
    go.hidden = true;
    claimBtn.hidden = false;
    claimBtn.disabled = false;
    againBtn.hidden = !canRespin || respun;
    againBtn.disabled = false;
  }

  function runDraw(onDone) {
    spinning = true;
    return draw().then(function (res) {
      spinning = false;
      if (!res || res.ok !== true) {
        announce(REASONS[res && res.reason] || 'Could not spin just now. Please try again.', true);
        return;
      }
      return spinTo(res.index).then(function () {
        confetti();
        selectedPrize = cfg.prizes[res.index] || null;
        onDone(selectedPrize || {}, !!res.canRespin);
      });
    }).catch(function () {
      spinning = false;
      announce('Could not reach the draw. Please try again.', true);
    });
  }

  go.addEventListener('click', function () {
    if (spinning) return;
    go.disabled = true;
    runDraw(function (prize, canRespin) { reveal(prize, canRespin); });
  });

  againBtn.addEventListener('click', function () {
    if (spinning) return;
    claimBtn.disabled = true;
    againBtn.disabled = true;
    /* Popup closes before the respin, not after — otherwise the wheel is
     * spinning behind a scrim the customer can't see through, which reads as
     * nothing happening. reveal() brings the panel back once it stops. */
    panel.hidden = true;
    runDraw(function (prize) {
      respun = true;
      reveal(prize, false);
    });
  });

  claimBtn.addEventListener('click', function () {
    claimBtn.disabled = true;
    againBtn.disabled = true;
    claim().then(function (res) {
      if (!res || res.ok !== true) {
        claimBtn.disabled = false;
        againBtn.disabled = !againBtn.hidden;
        announce(REASONS[res && res.reason] || 'Could not claim just now. Please try again.', true);
        return;
      }
      /* Closes the entry: once claimed, neither button has anything left to
       * do — a second click cannot mint a second code. */
      claimBtn.hidden = true;
      againBtn.hidden = true;
      codeWrap.hidden = false;
      codeText.textContent = res.code;
      copyBtn.hidden = false;
      /* Tells the modal controller (bottom of this file) the entry is done,
       * so it stops auto-opening the popup on future visits. A plain custom
       * event rather than a shared function call: this code shouldn't need
       * to know whether it's running inside the dialogue, a page, or both. */
      document.dispatchEvent(new CustomEvent('ef-spin-claimed'));
    }).catch(function () {
      claimBtn.disabled = false;
      againBtn.disabled = !againBtn.hidden;
      announce('Could not reach the server. Please try again.', true);
    });
  });

  if (copyBtn) {
    copyBtn.addEventListener('click', function () {
      var text = codeText.textContent;
      if (!text || !navigator.clipboard) return;
      navigator.clipboard.writeText(text).then(function () {
        var was = copyBtn.textContent;
        copyBtn.textContent = 'Copied';
        setTimeout(function () { copyBtn.textContent = was; }, 1500);
      });
    });
  }
})();


/* ---------------------------------------------------------------------------
 * The dialogue controller — spin.popup()'s <dialog>, opened here.
 *
 * Deliberately NOT the same rule as ef-merdeka.js's promodal, which marks
 * itself "seen" the instant it's shown (right, for a one-time announcement).
 * This one is shown-until-actioned: only a successful claim — the
 * 'ef-spin-claimed' event dispatched above — marks it done. Closing it via
 * the X, Esc, or a backdrop click does NOT suppress it; it comes back next
 * visit. That's not an oversight, it's the brief: "only pop up until it has
 * been spun and claimed, after it won't show up."
 * ------------------------------------------------------------------------- */
(function () {
  'use strict';

  var dlg = document.querySelector('[data-ef-spinmodal]');
  if (!dlg || typeof dlg.showModal !== 'function') return;

  /* Keyed to the campaign's open epoch, not the site — a future Spin & Save
   * with a different date starts fresh without anyone clearing storage. */
  var KEY = 'ef-spinmodal-' + (dlg.getAttribute('data-ef-spinmodal') || 'v1');

  function done() {
    try { return localStorage.getItem(KEY) === '1'; } catch (e) { return false; }
  }
  function remember() {
    /* Private windows and blocked site data THROW on write; swallow it
     * rather than leave the claim handler above in a broken state. */
    try { localStorage.setItem(KEY, '1'); } catch (e) {}
  }

  function close() {
    if (dlg.open) dlg.close();
  }

  Array.prototype.forEach.call(
    dlg.querySelectorAll('[data-ef-spinmodal-close]'),
    function (b) { b.addEventListener('click', close); });

  /* Esc fires 'cancel' before 'close' — either way, no remember() here; see
   * the file-level note on why closing must not suppress future visits. */
  dlg.addEventListener('cancel', function () {});

  /* Tells ef-merdeka.js this dialogue just closed, however that happened —
   * the X, Esc, a backdrop click, or a claim followed by any of those, since
   * all of them fire the dialog's own native 'close' event and this needs no
   * per-path duplicate. That dialogue reacts immediately if it's this
   * visitor's turn (see its own gate: seen() and spin done()), rather than
   * only re-checking on a future page load — the user's own words were
   * "spin pop up 1st then close then the second pop up ... will show up". */
  dlg.addEventListener('close', function () {
    document.dispatchEvent(new CustomEvent('ef-spin-dialog-closed'));
  });

  /* Clicking the backdrop. A <dialog>'s backdrop is part of the element, so
   * a click outside .ef-spin (the actual card) but inside the dialog is a
   * backdrop click — comparing against the card's own box is what tells
   * them apart. */
  dlg.addEventListener('click', function (e) {
    var box = dlg.querySelector('.ef-spin');
    if (!box) return;
    var r = box.getBoundingClientRect();
    var inside = e.clientX >= r.left && e.clientX <= r.right &&
                 e.clientY >= r.top && e.clientY <= r.bottom;
    if (!inside) close();
  });

  document.addEventListener('ef-spin-claimed', remember);

  /* The Merdeka offer dialogue's own "Spin & Save" button (promo.py's
   * popup(), data-ef-spin-open) opens THIS dialog instead of linking to a
   * page that doesn't exist. Delegated on document, not queried once at
   * load, because that button lives in a different dialogue this file
   * knows nothing else about. Re-checks done() at click time rather than
   * trusting whatever it was at page load — a second, independent entry
   * point into "open the wheel" is exactly the kind of place a stale check
   * would quietly let someone spin twice. */
  document.addEventListener('click', function (e) {
    var opener = e.target.closest && e.target.closest('[data-ef-spin-open]');
    if (!opener) return;
    if (done()) return;
    var other = opener.closest('dialog');
    if (other && other !== dlg && other.open) other.close();
    if (!dlg.open) dlg.showModal();
  });

  if (done()) return;

  /* A beat after load, same as the promodal — opening during first paint
   * fights the page for attention and reads as an error. */
  window.setTimeout(function () {
    if (dlg.open || done()) return;
    dlg.showModal();
  }, 1200);
})();
